import LoadingButton from "@mui/lab/LoadingButton";
import { Alert, Box, SxProps, Theme, useTheme } from "@mui/material";
import { useEffect, useState } from "react";

import BoardSelect from "./BoardSelect";
import { fetchMyBoards, pinAd } from "~src/api/creativeCenter";

type Props = {
  adId: string;
  sx?: SxProps<Theme>;
};

export type Board = {
  id: number;
  user_id: string;
  title: string;
  description?: string | null;
};

export default function SaveAdToTrendRocket(props: Props) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [successAlertOpen, setSuccessAlertOpen] = useState(false);
  const [errorAlertOpen, setErrorAlertOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [boardId, setBoardId] = useState<number>();
  const [boards, setBoards] = useState<Board[]>();

  useEffect(() => {
    const fetchBoard = async () => {
      const response = await fetchMyBoards();
      if (response) {
        setBoards(response.results);
      }
    };
    fetchBoard();
  }, []);

  const saveAd = async (e: React.FormEvent<HTMLFormElement>) => {
    setErrorAlertOpen(false);
    setSuccessAlertOpen(false);
    e.preventDefault();
    setLoading(true);
    await pinAd(boardId, props.adId)
      .then(() => {
        setBoardId(undefined);
        setSuccessAlertOpen(true);
      })
      .catch((error) => {
        setErrorAlertOpen(true);
        const errorMsg = error.response?.data?.error || 
                         error.message || 
                         "An error occurred while saving the ad";
        setErrorMessage(errorMsg);
      });
    setLoading(false);
  };

  const closeErrorAlert = () => {
    setErrorAlertOpen(false);
    setErrorMessage(undefined);
  };

  return (
    <form onSubmit={saveAd}>
      <Box
        sx={{
          display: "flex !important",
          alignItems: "center",
          justifyContent: "center",
          "& .MuiFormControl-root": {
            width: "50%",
            mr: "10px",
          },
          ...props.sx,
        }}
        data-testid="save-ad"
      >
        <BoardSelect
          name="Board"
          boards={boards}
          value={boardId}
          onChange={setBoardId}
          required
        />
        <LoadingButton
          sx={{
            width: "50%",
            mr: "0px !important",
            mt: "0px !important",
            background: `${theme.palette.common.black} !important`,
            color: theme.palette.common.white,
            borderRadius: 10,
            opacity: boardId ? 1 : 0.5,
            "&:hover": {
              background: `${theme.palette.primary.dark} !important`,
            },
            ".MuiLoadingButton-loadingIndicator": {
              color: `${theme.palette.common.white} !important`,
            },
          }}
          disabled={boardId ? false : true}
          loading={loading}
          loadingPosition="center"
          type="submit"
        >
          Save Ad
        </LoadingButton>
      </Box>
      {successAlertOpen && (
        <Alert
          sx={{
            mt: "10px",
            "& .MuiAlert-action": {
              "& .MuiButtonBase-root": {
                border: "none !important",
                backgroundColor: "transparent !important",
              },
            },
          }}
          severity="success"
          onClose={() => setSuccessAlertOpen(false)}
        >
          Ad pinned to this board!
        </Alert>
      )}
      {errorAlertOpen && (
        <Alert
          sx={{
            mt: "10px",
            "& .MuiAlert-action": {
              "& .MuiButtonBase-root": {
                border: "none !important",
                backgroundColor: "transparent !important",
              },
            },
          }}
          severity="error"
          onClose={closeErrorAlert}
        >
          {errorMessage}
        </Alert>
      )}
    </form>
  );
}
